import {
  type BillingInterval,
  isBillingInterval,
  isPlanTier,
  type PlanTier,
} from "@reactive-resume/utils";

export type SelectedPlan = {
  plan: NonNullable<PlanTier>;
  interval: BillingInterval;
};

const STORAGE_KEY = "careerbldr:selected-plan";
const DEFAULT_INTERVAL: BillingInterval = "monthly";

const parseSelection = (plan: string | null, interval: string | null): SelectedPlan | null => {
  if (!isPlanTier(plan)) return null;
  return { plan, interval: isBillingInterval(interval) ? interval : DEFAULT_INTERVAL };
};

export const checkoutPathForPlan = (
  plan: NonNullable<PlanTier>,
  interval: BillingInterval,
): string =>
  `/dashboard/checkout?plan=${encodeURIComponent(plan)}&interval=${encodeURIComponent(interval)}`;

export const parseSelectedPlanFromSearch = (
  search: string | URLSearchParams,
): SelectedPlan | null => {
  const params =
    typeof search === "string"
      ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
      : search;
  return parseSelection(params.get("plan"), params.get("interval"));
};

const safeSession = (): Storage | undefined => {
  try {
    return typeof window === "undefined" ? undefined : window.sessionStorage;
  } catch {
    return undefined;
  }
};

export const stashSelectedPlan = (selected: SelectedPlan): void => {
  const ss = safeSession();
  if (!ss) return;
  try {
    ss.setItem(STORAGE_KEY, JSON.stringify(selected));
  } catch {
    // ignore quota / privacy-mode failures
  }
};

export const stashSelectedPlanFromSearch = (
  search?: string | URLSearchParams,
): SelectedPlan | null => {
  const source =
    search ?? (typeof window === "undefined" ? "" : window.location.search);
  const parsed = parseSelectedPlanFromSearch(source);
  if (parsed) stashSelectedPlan(parsed);
  return parsed;
};

export const peekSelectedPlan = (): SelectedPlan | null => {
  const ss = safeSession();
  if (!ss) return null;
  try {
    const raw = ss.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as { plan?: string; interval?: string };
    return parseSelection(data.plan ?? null, data.interval ?? null);
  } catch {
    return null;
  }
};

export const consumeSelectedPlan = (): SelectedPlan | null => {
  const parsed = peekSelectedPlan();
  const ss = safeSession();
  try {
    ss?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
  return parsed;
};

/** Prefer the current URL, then the session stash. Stashes a URL plan when present. */
export const resolveSelectedPlan = (): SelectedPlan | null => {
  if (typeof window !== "undefined") {
    const fromUrl = stashSelectedPlanFromSearch(window.location.search);
    if (fromUrl) return fromUrl;
  }
  return peekSelectedPlan();
};

export const postAuthPath = (planTier: PlanTier | null | undefined, fallback: string): string => {
  if (planTier) return fallback;
  const selected = consumeSelectedPlan();
  if (selected) return checkoutPathForPlan(selected.plan, selected.interval);
  return fallback;
};
